import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { utcToZonedTime, formatInTimeZone } from 'date-fns-tz';
import { cancelAppointment, rescheduleAppointment, getAvailability, getCalendarAvailability, getManageAppointment, getPublicProfile, updateManageAppointmentChannels, type ManageAppointmentDetails, type PublicOptionalClientChannelKey } from '../../shared/api/bookingApi.js';
import type { ExternalContact, TimeSlot } from '@minibook/shared';
import { buildExternalContactHref, formatPhone, getExternalContactDisplayLabel, getExternalContactPlatformMeta } from '@minibook/shared';
import { ApiError } from '../../shared/api/client.js';
import { AppIcon, type IconName } from '../../shared/ui/Icon';
import { useTranslation } from '../../i18n/useTranslation';
import { formatDuration, formatPrice, getIntlLocale, getPriceLocale } from '../../shared/formatting.js';
import { DEFAULT_TIMEZONE } from '../../shared/constants/booking.js';
import { socialBrandIconCssColor } from '../../shared/socialBrand.js';
import { ClientNotificationChannelsCard } from '../../shared/components/ClientNotificationChannelsCard.js';
import NotFound from './NotFound';
import { devWarn } from '../../shared/lib/devLog.js';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { getPublicSiteStage } from '../../shared/config/siteStage.js';
import '../styles/booking.css';

type ActionView = 'details' | 'cancel' | 'reschedule';

export default function ManagePage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const appointmentId = searchParams.get('appointmentId');
  const urlToken = searchParams.get('token');
  const [resolvedToken, setResolvedToken] = useState<string | null>(urlToken);
  const [resolvedAppointmentId, setResolvedAppointmentId] = useState<string | null>(appointmentId);

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profileNotFound, setProfileNotFound] = useState(false);
  const [view, setView] = useState<ActionView>('details');
  const [manageDetails, setManageDetails] = useState<ManageAppointmentDetails | null>(null);
  const [pendingChannel, setPendingChannel] = useState<PublicOptionalClientChannelKey | null>(null);
  const [pendingEmailRemoval, setPendingEmailRemoval] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [availableDates, setAvailableDates] = useState<Set<string> | null>(null);
  const slotsAbortControllerRef = useRef<AbortController | null>(null);
  const slotsRequestIdRef = useRef(0);

  useEffect(() => {
    if (appointmentId) {
      setResolvedAppointmentId(appointmentId);
    }
    if (urlToken) {
      setResolvedToken(urlToken);
    }
  }, [appointmentId, urlToken]);

  const calendarTimeZone = manageDetails?.master?.timeZone || DEFAULT_TIMEZONE;

  // Ключ даты строго в TZ мастера (устраняет сдвиги при разном TZ клиента/мастера)
  const formatDateKey = (date: Date): string => {
    return formatInTimeZone(date, calendarTimeZone, 'yyyy-MM-dd');
  };


  // Загружаем профиль для получения языка мастера
  useEffect(() => {
    if (!slug) return;
    getPublicProfile(slug)
      .then(() => {
        setProfileNotFound(false);
      })
      .catch((err: any) => {
        if (err?.error?.code === 'not_found') {
          setProfileNotFound(true);
          return;
        }
      });
  }, [slug]);

  const loadManageDetails = useCallback(async () => {
    if (!resolvedAppointmentId || !resolvedToken) return;

    setDetailsLoading(true);
    setError(null);
    try {
      const data = await getManageAppointment(resolvedAppointmentId, resolvedToken);
      setManageDetails(data);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error?.message || t('booking.manage.loadError'));
    } finally {
      setDetailsLoading(false);
    }
  }, [resolvedAppointmentId, resolvedToken, t]);

  useEffect(() => {
    if (!resolvedAppointmentId || !resolvedToken) return;
    void loadManageDetails();
  }, [resolvedAppointmentId, resolvedToken, loadManageDetails]);

  useEffect(() => {
    if (!resolvedAppointmentId || !resolvedToken) return undefined;

    const handleFocus = () => {
      void loadManageDetails();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [resolvedAppointmentId, resolvedToken, loadManageDetails]);

  const today = useMemo(() => {
    const zonedNow = utcToZonedTime(new Date(), calendarTimeZone);
    zonedNow.setHours(0, 0, 0, 0);
    return zonedNow;
  }, [calendarTimeZone]);

  const daysRange = useMemo(() => {
    const days: Date[] = [];
    for (let i = 0; i < 21; i += 1) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }
    return days;
  }, [today]);

  const weekdays = useMemo(() => {
    const locale = getIntlLocale(language);
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: calendarTimeZone });
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      return formatter.format(date);
    });
  }, [today, language, calendarTimeZone]);

  // Перенос записи всегда service-first: только непустой массив serviceIds
  const rescheduleServiceIds = useMemo(() => {
    if (!manageDetails) return [];
    const ids = manageDetails.appointment.serviceIds;
    if (ids?.length) return ids;
    return (manageDetails.appointment.orderedServices?.map((os) => (os as { id: string }).id).filter(Boolean) ?? []) as string[];
  }, [manageDetails]);

  useEffect(() => {
    if (!slug || !manageDetails) return;
    if (rescheduleServiceIds.length === 0) {
      setAvailableDates(new Set());
      return;
    }

    const from = daysRange[0] ? formatDateKey(daysRange[0]) : undefined;
    const to = daysRange[daysRange.length - 1] ? formatDateKey(daysRange[daysRange.length - 1]) : undefined;
    if (!from || !to) return;

    setAvailableDates(null);
    getCalendarAvailability(slug, { from, to, serviceIds: rescheduleServiceIds })
      .then((response) => {
        setAvailableDates(new Set(response.availableDates));
      })
      .catch(() => {
        setAvailableDates(new Set());
      });
  }, [slug, manageDetails, rescheduleServiceIds, daysRange]);

  const handleCancel = async () => {
    if (!resolvedAppointmentId || !resolvedToken) {
      setError(t('booking.manage.paramsMissing'));
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      await cancelAppointment(resolvedAppointmentId, resolvedToken);
      navigate(`/booking/${slug}/success?canceled=true`);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error.message || t('booking.manage.cancelError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReschedule = async () => {
    if (!resolvedAppointmentId || !resolvedToken || !selectedSlot) {
      setError(t('booking.manage.selectTime'));
      return;
    }

    setActionLoading(true);
    setError(null);

    try {
      await rescheduleAppointment(resolvedAppointmentId, resolvedToken, selectedSlot.startAt);
      navigate(`/booking/${slug}/success?rescheduled=true`);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error.message || t('booking.manage.rescheduleError'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleChannelToggle = useCallback(
    async (channel: PublicOptionalClientChannelKey, enabled: boolean) => {
      if (!resolvedAppointmentId || !resolvedToken) return;
      setPendingChannel(channel);
      try {
        const result = await updateManageAppointmentChannels(resolvedToken, {
          channels: [{ channel, enabled }],
        });
        setManageDetails((prev) => (
          prev
            ? { ...prev, clientChannels: result.clientChannels, clientEmail: result.clientEmail }
            : prev
        ));
      } catch (err) {
        const apiError = err as ApiError;
        setError(apiError.error?.message || t('booking.manage.loadError'));
      } finally {
        setPendingChannel(null);
      }
    },
    [resolvedAppointmentId, resolvedToken, t]
  );

  const handleEmailRemove = useCallback(async () => {
    if (!resolvedToken) return;
    setPendingEmailRemoval(true);
    try {
      const result = await updateManageAppointmentChannels(resolvedToken, {
        channels: [{ channel: 'email', enabled: false }],
      });
      setManageDetails((prev) => (
        prev
          ? { ...prev, clientChannels: result.clientChannels, clientEmail: result.clientEmail }
          : prev
      ));
      setError(null);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.error?.message || t('booking.manage.loadError'));
    } finally {
      setPendingEmailRemoval(false);
    }
  }, [resolvedToken, t]);

  const handleDateSelect = async (date: string) => {
    if (!slug || !manageDetails) return;
    const requestId = slotsRequestIdRef.current + 1;
    slotsRequestIdRef.current = requestId;
    slotsAbortControllerRef.current?.abort();
    const controller = new AbortController();
    slotsAbortControllerRef.current = controller;
    setSelectedDate(date);
    setSelectedSlot(null);
    setSlotsLoading(true);
    setError(null);

    if (rescheduleServiceIds.length === 0) {
      setAvailableSlots([]);
      setSlotsLoading(false);
      return;
    }

    try {
      const response = await getAvailability(slug, {
        serviceIds: rescheduleServiceIds,
        date,
      }, { signal: controller.signal });
      if (controller.signal.aborted || slotsRequestIdRef.current !== requestId) return;
      setAvailableSlots(response.slots as TimeSlot[]);
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      if (slotsRequestIdRef.current !== requestId) return;
      const apiError = err as ApiError;
      setError(apiError.error.message || t('booking.manage.slotsError'));
    } finally {
      if (slotsRequestIdRef.current !== requestId) return;
      setSlotsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      slotsAbortControllerRef.current?.abort();
    };
  }, []);

  const formatDate = (isoString: string, timeZone?: string) => {
    const date = new Date(isoString);
    const locale = getIntlLocale(language);
    return date.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: timeZone || DEFAULT_TIMEZONE,
    });
  };

  const formatTime = (isoString: string, timeZone?: string) => {
    const date = new Date(isoString);
    const locale = getIntlLocale(language);
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZone || DEFAULT_TIMEZONE,
    });
  };

  const priceLocale = getPriceLocale(language);
  const appointment = manageDetails?.appointment;
  const master = manageDetails?.master;
  const timeZone = master?.timeZone;
  const serviceList = appointment?.orderedServices || [];
  const contactLinks = useMemo(() => {
    const isRuContour = getPublicSiteStage() === 'ru';
    const rawItems = [...(master?.socialLinks || []), ...(master?.messengerLinks || [])];
    const items = isRuContour ? rawItems : rawItems.filter((c) => c.platform !== 'max');
    const fallback: ExternalContact[] = [];
    if (items.length === 0) {
      if (master?.vkUrl) fallback.push({ platform: 'vk', value: master.vkUrl });
      if (master?.tgUsername) fallback.push({ platform: 'telegram', value: `@${master.tgUsername.replace(/^@+/, '')}` });
      if (isRuContour && master?.max) fallback.push({ platform: 'max', value: master.max });
    }
    return (items.length > 0 ? items : fallback)
      .map((contact) => ({
        contact,
        href: buildExternalContactHref(contact),
        label: getExternalContactDisplayLabel(contact),
        icon: getExternalContactPlatformMeta(contact.platform).icon as IconName,
      }))
      .filter((item) => !!item.href);
  }, [master?.max, master?.messengerLinks, master?.socialLinks, master?.tgUsername, master?.vkUrl]);

  if (profileNotFound) {
    return <NotFound />;
  }

  if (!resolvedAppointmentId || !resolvedToken) {
    return (
      <div className="booking-container" style={{ textAlign: 'center' }}>
        <div className="booking-card">
          <div className="booking-card-lang-row">
            <LanguageSwitcher />
          </div>
          <h1 className="booking-heading">{t('booking.manage.error')}</h1>
          <p>{error || t('booking.manage.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-container">
      <div className="booking-card">
        <div className="booking-card-lang-row">
          <LanguageSwitcher />
        </div>
        <h1 className="booking-title booking-mb-lg booking-font-semibold">
          {t('booking.manage.title')}
        </h1>

        {error && (
          <div className="booking-alert booking-alert-error">
            {error}
          </div>
        )}

        {detailsLoading && (
          <div className="booking-loading">{t('booking.manage.loadingDetails')}</div>
        )}

        {appointment && master && (
          <>
            <div className="booking-manage-summary">
              <div className="booking-manage-summary-title">{t('booking.manage.booked')} ✅</div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.manage.name')}:</span>
                <span>{appointment.clientName}</span>
              </div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.success.phone')}:</span>
                <span>{appointment.clientPhone ? formatPhone(appointment.clientPhone) : '—'}</span>
              </div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.manage.date')}:</span>
                <span>{formatDate(appointment.startAt, timeZone)}</span>
              </div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.manage.time')}:</span>
                <span>{formatTime(appointment.startAt, timeZone)} - {formatTime(appointment.endAt, timeZone)}</span>
              </div>
              <div className="booking-manage-summary-row">
                <span className="booking-manage-summary-label">{t('booking.manage.services')}:</span>
                <div className="booking-flex-col booking-gap-sm" style={{ flex: 1 }}>
                  {serviceList.length > 0 ? (
                    serviceList.map((service, index) => (
                      <div key={index} className="booking-flex-between" style={{ gap: 'var(--spacing-md)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', flex: 1, minWidth: 0 }}>
                          <span style={{ flex: 1, minWidth: 0 }}>{service.name}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)', flexShrink: 0 }}>
                            <AppIcon name="timeOutline" size={16} tone="muted" />
                            <span className="booking-text-sm" style={{ color: 'var(--color-text-muted)' }}>
                              {formatDuration(service.durationMin, priceLocale)}
                            </span>
                          </div>
                        </div>
                        <div className="booking-text-sm booking-font-semibold" style={{ color: 'var(--color-text-primary)', flexShrink: 0, textAlign: 'right' }}>
                          {formatPrice(Number(service.price), priceLocale)} {t('booking.currency')}
                        </div>
                      </div>
                    ))
                  ) : (
                    <span>—</span>
                  )}
                </div>
              </div>
            </div>

            <div className="booking-manage-master">
              <div className="booking-manage-master-title">
                {t('booking.manage.recordTo')}: {master.publicTitle || master.name || '—'}
              </div>
              <div className="booking-manage-master-contacts">
                <div className="booking-manage-master-contact">
                  <AppIcon name="phone" size={16} tone="muted" />
                  <span>{master.phone ? formatPhone(master.phone) : '—'}</span>
                </div>
                <div className="booking-manage-master-contact">
                  <AppIcon name="location" size={16} tone="muted" />
                  <span>{master.address || '—'}</span>
                </div>
                <div className="booking-manage-master-socials">
                  {contactLinks.map(({ contact, href, label, icon }) => (
                    <a
                      key={`${contact.platform}-${contact.value}`}
                      href={href || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="booking-manage-social-btn"
                      title={label}
                      aria-label={label}
                    >
                      <AppIcon name={icon} size={24} tone="default" style={{ color: socialBrandIconCssColor(contact.platform) }} />
                    </a>
                  ))}
                  {contactLinks.length === 0 && (
                    <span>—</span>
                  )}
                </div>
              </div>
            </div>

            <ClientNotificationChannelsCard
              channels={manageDetails.clientChannels}
              clientEmail={manageDetails.clientEmail || undefined}
              pendingChannel={pendingChannel}
              pendingEmailRemoval={pendingEmailRemoval}
              onToggle={handleChannelToggle}
              onEmailRemove={handleEmailRemove}
            />

            {view === 'details' && (
              <div className="booking-manage-actions">
                <button
                  className="booking-button booking-button-danger"
                  type="button"
                  onClick={() => setView('cancel')}
                >
                  {t('booking.manage.cancel')}
                </button>
                <button
                  className="booking-button booking-button-primary"
                  type="button"
                  onClick={() => setView('reschedule')}
                >
                  {t('booking.manage.reschedule')}
                </button>
              </div>
            )}

            {view === 'cancel' && (
              <div className="booking-manage-confirm">
                <div className="booking-manage-confirm-text">
                  {t('booking.manage.cancelConfirm')}
                </div>
                <div className="booking-manage-actions">
                  <button
                    className="booking-button booking-button-secondary"
                    type="button"
                    onClick={() => setView('details')}
                  >
                    {t('booking.manage.back')}
                  </button>
                  <button
                    className="booking-button booking-button-danger"
                    type="button"
                    onClick={handleCancel}
                    disabled={actionLoading}
                  >
                    {actionLoading ? t('booking.manage.canceling') : t('booking.manage.cancelAppointment')}
                  </button>
                </div>
              </div>
            )}

            {view === 'reschedule' && (
              <div className="booking-manage-reschedule">
                <div className="booking-manage-section-title">{t('booking.manage.selectNewDate')}</div>
                <div className="booking-manage-weekdays">
                  {weekdays.map((day, index) => {
                    const weekdayDate = new Date(today);
                    weekdayDate.setDate(today.getDate() + index);
                    const isWeekend = [0, 6].includes(weekdayDate.getDay());
                    return (
                      <div key={day} className={isWeekend ? 'is-weekend' : ''}>
                        {day}
                      </div>
                    );
                  })}
                </div>
                <div className="booking-manage-days">
                  {daysRange.map((date) => {
                    const dateStr = formatDateKey(date);
                    const isSelected = selectedDate === dateStr;
                    const isToday = dateStr === formatDateKey(today);
                    const isWeekend = [0, 6].includes(date.getDay());
                    const isDisabled = availableDates ? !availableDates.has(dateStr) : true;
                    return (
                      <button
                        key={dateStr}
                        type="button"
                        className={`booking-manage-day${isSelected ? ' is-selected' : ''}${isToday ? ' is-today' : ''}${isDisabled ? ' is-disabled' : ''}${isWeekend ? ' is-weekend' : ''}`}
                        onClick={() => {
                          if (isDisabled) return;
                          handleDateSelect(dateStr);
                        }}
                        disabled={isDisabled}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>

                <div className="booking-manage-section-title">{t('booking.manage.timeLabel')}</div>
                {slotsLoading && <div className="booking-loading">{t('booking.manage.loadingSlots')}</div>}
                {!slotsLoading && selectedDate && availableSlots.length === 0 && (
                  <div className="booking-empty-state">{t('booking.manage.noSlots')}</div>
                )}
                {!slotsLoading && availableSlots.length > 0 && (
                  <div className="booking-manage-slots">
                    {availableSlots.map((slot, index) => {
                      const isSelected = selectedSlot?.startAt === slot.startAt;
                      if (import.meta.env.DEV && slot.endAt == null) {
                        devWarn('[ManagePage] Ожидается service-first слот с endAt', slot);
                      }
                      const timeLabel = slot.endAt != null
                        ? `${formatTime(slot.startAt, timeZone)} – ${formatTime(slot.endAt, timeZone)}`
                        : formatTime(slot.startAt, timeZone);
                      return (
                        <button
                          key={`${slot.startAt}-${index}`}
                          type="button"
                          className={`booking-manage-slot${isSelected ? ' is-selected' : ''}`}
                          onClick={() => setSelectedSlot(slot)}
                        >
                          {timeLabel}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="booking-manage-actions">
                  <button
                    className="booking-button booking-button-secondary"
                    type="button"
                    onClick={() => setView('details')}
                  >
                    {t('booking.manage.back')}
                  </button>
                  <button
                    className="booking-button booking-button-primary"
                    type="button"
                    onClick={handleReschedule}
                    disabled={actionLoading || !selectedSlot}
                  >
                    {actionLoading ? t('booking.manage.rescheduling') : t('booking.manage.rescheduleAppointment')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
